import { BASE_URL } from "../utils/requestUtils";

export interface GetLabsServiceAreaResponse {
  zip: string;
  served: boolean;
}

export interface GetLabsReadinessResponse {
  enabled: boolean;
}

export interface GetLabsAvailabilitySlot {
  key: string | null;
  start: string;
  end: string;
}

export interface GetLabsAvailabilityDay {
  date: string;
  slots: GetLabsAvailabilitySlot[];
}

export interface GetLabsAvailabilityResponse {
  serviceable: boolean;
  timezone?: string | null;
  days: GetLabsAvailabilityDay[];
}

/**
 * Read-only GetLabs scheduling surfaces. Booking remains in the later
 * submission flow so this layer cannot create an appointment.
 */
export class GetLabsAvailabilityApiService {
  private static readonly READINESS_PATH = "/api/v1/getlabs_readiness";
  private static readonly SERVICE_AREA_PATH = "/api/v1/getlabs_service_area";
  private static readonly AVAILABILITY_PATH = "/api/v1/getlabs_availability";

  static async getReadiness(): Promise<GetLabsReadinessResponse> {
    const response = await fetch(`${BASE_URL}${this.READINESS_PATH}/`, {
      signal: AbortSignal.timeout(10_000),
    });
    if (!response.ok) {
      throw new Error("GetLabs readiness check failed");
    }
    return response.json() as Promise<GetLabsReadinessResponse>;
  }

  static async checkServiceArea(
    zipCode: string,
  ): Promise<GetLabsServiceAreaResponse> {
    const params = new URLSearchParams({ zip: zipCode });
    const response = await fetch(
      `${BASE_URL}${this.SERVICE_AREA_PATH}/?${params}`,
      { signal: AbortSignal.timeout(20_000) },
    );
    if (!response.ok) {
      throw new Error("GetLabs service-area check failed");
    }
    return response.json() as Promise<GetLabsServiceAreaResponse>;
  }

  static async getAvailability(input: {
    street: string;
    unit?: string;
    city: string;
    state: string;
    zip: string;
    fromDate: string;
    days?: number;
  }): Promise<GetLabsAvailabilityResponse> {
    const response = await fetch(`${BASE_URL}${this.AVAILABILITY_PATH}/`, {
      method: "POST",
      signal: AbortSignal.timeout(20_000),
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        street: input.street,
        unit: input.unit || "",
        city: input.city,
        state: input.state,
        zip: input.zip,
        from_date: input.fromDate,
        days: input.days ?? 7,
      }),
    });
    if (!response.ok) {
      throw new Error("GetLabs availability check failed");
    }
    return response.json() as Promise<GetLabsAvailabilityResponse>;
  }
}
